import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, OrderLineModel } from "@insite/client-framework/Types/ApiModels";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Img, { ImgProps } from "@insite/mobius/Img";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import loadInventoryForDataLayer from "@MasterBluePrint/Handlers/LoadInventoryForDataLayer";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const currentPageUrl = location.pathname;
    const path = location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname;
    const catalogPage = getCatalogPageStateByPath(state, path).value;
    return {
        category: getCategoryState(state, catalogPage?.categoryIdWithBrandId ?? catalogPage?.categoryId).value,
        currentPageUrl,
    };
};

const mapDispatchToProps = {
    loadInventoryForDataLayer,
};

interface OwnProps {
    carouselType: string;
    product: CartLineModel | ProductContextModel;
    extendedStyles?: ProductImageStyles;
    carouselTitle: string;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductImageStyles {
    linkWrappingImage?: ClickablePresentationProps;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
}

export const productImageStyles: ProductImageStyles = {
    linkWrappingImage: {
        css: css`
            border: 1px solid #ececec;
            overflow: hidden;
            @media print {
                display: flex !important;
            }
            @media screen and (max-width: 1399px) {
                max-width: 9.6875rem;
                max-height: 9.6875rem;
            }
        `,
    },
    image: {
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    img: {
        css: css`
            height: 100%;
            width: 100%;
            margin: 0 auto;
            max-width: 9.6875rem;
            max-height: 9.6875rem;
        `,
    },
};

const ProductImage: FC<Props> = ({
    carouselType,
    product,
    extendedStyles,
    category,
    currentPageUrl,
    carouselTitle,
    loadInventoryForDataLayer,
}) => {
    const [styles] = React.useState(() => mergeToNew(productImageStyles, extendedStyles));

    let altText = "product" in product ? product.product.imageAltText : product.altText;
    if (!altText || altText === "") {
        altText = "product" in product ? product.product.productTitle : product.productName;
    }
    altText += ` ${translate("redirect to product page")}`;

    const imagePath =
        "product" in product
            ? product.product.mediumImagePath || product.product.smallImagePath
            : product.smallImagePath;

    const productId = "product" in product ? product.product.id : "productId" in product ? product.productId : "";
    const unitOfMeasure =
        "product" in product
            ? product.productInfo?.unitOfMeasure
            : "unitOfMeasure" in product
            ? product.unitOfMeasure
            : "";

    const productClickDataLayerEvent = () => {
        if (productId) {
            loadInventoryForDataLayer({
                productId,
                listType: carouselType,
                unitOfMeasure,
                product,
                currentPageUrl,
                carouselTitle,
                category,
                itemLinkClick: 'Product Image',
            });
        }
    };

    const productDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;
    const loading = product.idx === undefined ? "lazy" : product.idx < 3 ? "eager" : "lazy";

    return (
        <Clickable
            {...styles.linkWrappingImage}
            href={productDetailPath}
            data-test-selector="productImage"
            onClick={productClickDataLayerEvent}
        >
            <Img {...styles.img} loading={loading} src={imagePath} altText={altText} />
        </Clickable>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductImage);
